import { CheckoutState, possibleNavigationSteps } from './index';
import { MutationTree } from 'vuex';
import CheckoutSettings from "./interfaces/checkoutSettings";
import CheckoutSummary from "./interfaces/checkoutSummary";
import PaymentOptions from "@store/checkout/interfaces/paymentOptions";
import PriceSummary from "./interfaces/priceSummary";
import * as Tracking from "@lib/tracking/index";
import ServiceItem from "./interfaces/serviceItem";
import CheckoutItem from "./interfaces/checkoutItem";
import WhitelabelSettings from "./interfaces/whitelabelSettings";
// @ts-ignore
import { updateField } from 'vuex-map-fields';
import PreInquiry from "./interfaces/preInquiry";
import { CHECKOUT_DEFAULT_LOADING_TEXT } from "@constants/loading";
import { BOOKING_STEPS } from "@constants/steps";
import BookingRequest from "@store/checkout/interfaces/bookingRequest";
import SiteSettings from "@store/checkout/interfaces/siteSettings";
import { CHECKOUT_API } from "@constants/api";
import makeAbsoluteUrl from "@store/helper/urlHelper";

const DEFAULT_DR_PERSONS = 2;
export default {
    updateField,
    initCheckoutSteps(state, bookingSteps: { [index: string]: any }) {
        state.navigation = possibleNavigationSteps.filter((bookingStep: string) => isNotOptional(bookingStep, bookingSteps) || isEnabled(bookingStep, bookingSteps))

        // avoid to set 'initial' first step (navigation[1]) in first place during restore.
        if (state.activeIndex === 1 && state.checkoutSettings.restoreId === '') {
            state.activeNavigation = state.navigation[1];
        }
    },
    setActiveNavigation(state, activeNavigation: string) {
        state.activeNavigation = activeNavigation;
    },
    setActiveIndex(state, activeIndex: number) {
        state.activeIndex = activeIndex;
    },
    incrementActiveIndex(state) {
        const maxLength = state.navigation.length - 1;
        if (state.activeIndex < maxLength) {
            state.activeIndex++;
        }
    },
    decrementActiveIndex(state) {
        if (state.activeIndex > 0) {
            state.activeIndex--;
        }
    },
    setApiEndpoint(state, apiEndpoint: string) {
        if (apiEndpoint !== '') {
            state.apiEndpoint = makeAbsoluteUrl(state.siteSettings.baseUrl, apiEndpoint);
        }
    },
    setApiToken(state, apiToken: string) {
        if (apiToken !== '') {
            state.apiToken = apiToken;
        }
    },
    setCheckoutSettings(state, checkoutSettings: CheckoutSettings) {
        state.checkoutSettings = checkoutSettings;
    },
    setInitialCheckoutSettings(state, checkoutSettings: CheckoutSettings) {
        state.initialCheckoutSettings = checkoutSettings;
    },
    setAppointmentHash(state, appointmentHash: string) {
        //appointmentHash (AppointmentAirportId) is used to fetch flights data, updateAppointment & fetch Appointment data
        state.checkoutSettings.appointmentHash = appointmentHash;
    },
    setAppointmentId(state, appointmentId: string) {
        //appointmentId (AppointmentFlightId) is used for mostly all requests to fetch prices and data
        state.checkoutSummary.appointmentId = appointmentId;
    },
    setIsValid(state, isValid: boolean) {
        state.isValid = isValid;
    },
    setIsWhitelabel(state, isWhitelabel: boolean) {
        state.isWhitelabel = isWhitelabel;
    },
    setCheckoutSummary(state, checkoutSummary: CheckoutSummary) {
        state.checkoutSummary = checkoutSummary;
    },
    setPriceSummary(state, priceSummary: PriceSummary) {
        state.priceSummary = priceSummary;
    },
    initCheckoutSummary(state) {
        const tripId: number = state.checkoutSettings.trip.id;
        const appointmentId: string = state.checkoutSettings.appointmentHash;
        const hsCompanyId: number = state.checkoutSettings.hsCompanyId;
        const stageIdent: string = state.checkoutSettings.stageIdent;
        const sessionId: string = state.checkoutSettings.sessionId;

        const checkoutSummary = state.checkoutSummary;
        checkoutSummary.tripId = tripId;
        checkoutSummary.appointmentId = appointmentId;
        checkoutSummary.hsCompanyId = hsCompanyId;
        checkoutSummary.tracking.stageIdent = stageIdent;
        checkoutSummary.tracking.awin = Tracking.getAWINCustomCookie();
        checkoutSummary.tracking.gtm = Tracking.getGTMCookie();
        checkoutSummary.tracking.travelagency = Tracking.getTravelagencyCookie();
        checkoutSummary.sessionId = sessionId;

        state.checkoutSummary = checkoutSummary;
    },
    setVisibility(state, { modal, isVisible }) {
        modal.isVisible = isVisible;
    },
    setModalContent(state, { modal, content }) {
        modal.content = content;
    },
    resetCheckoutSummary(state) {
        state.checkoutSummary.fare = state.fare;
        state.checkoutSummary.travellerCount.adults = DEFAULT_DR_PERSONS;
        state.checkoutSummary.rooms = [];
        state.checkoutSummary.services = [];
        state.checkoutSummary.carRentals = [];
        state.checkoutSummary.hotelUpgrades = [];
    },
    mapRoomsToCheckoutSummary(state, rooms) {
        state.checkoutSummary.rooms = rooms;
    },
    updateFareInCheckoutSummary(state, fare) {
        state.checkoutSummary.fare = fare;
    },
    setAdultTravellerCount(state, adults: number) {
        state.checkoutSummary.travellerCount.adults = adults;
        updateQuantityInDataLayer(adults);
    },
    increaseServiceAmount(state, service: ServiceItem) {
        const item: CheckoutItem | undefined = state.checkoutSummary.services.find((checkoutItem: CheckoutItem) => checkoutItem.id === service.id);

        if (item !== undefined) {
            item.amount++;
        } else {
            state.checkoutSummary.services.push({
                id: service.id,
                amount: 1
            });
        }
    },
    decreaseServiceAmount(state, service: ServiceItem) {
        const item: CheckoutItem | undefined = state.checkoutSummary.services.find((checkoutItem: CheckoutItem) => checkoutItem.id === service.id);

        if (item === undefined) {
            return;
        }

        item.amount--;
        if (item.amount === 0) {
            state.checkoutSummary.services.forEach((checkoutItem: CheckoutItem, index: number)=> {
                if (checkoutItem.id === item.id) {
                    state.checkoutSummary.services.splice(index, 1);
                }
            });
        }
    },
    // @ts-ignore
    mapAccommodationUpgradesToCheckoutSummary(state, accommodationUpgrades: CheckoutItem[]) {
        state.checkoutSummary.hotelUpgrades = accommodationUpgrades;
    },
    // @ts-ignore
    mapCarRentalsToCheckoutSummary(state, carRentals: CheckoutItem[]) {
        state.checkoutSummary.carRentals = carRentals;
    },
    setIsFinished(state, isFinished: boolean) {
        state.isFinished = isFinished;
    },
    setCheckout(state, path: string) {
        state.checkout = path;
    },
    setAgentImage(state, agentImage: string) {
        state.agentImage = agentImage;
    },
    setBindingMode(state, bindingMode: string) {
        state.checkoutSettings.bindingMode = bindingMode;
    },
    resetBindingMode(state) {
        state.checkoutSettings.bindingMode = state.initialCheckoutSettings.bindingMode;
    },
    setWhitelabelSettings(state, whitelabelSettings: WhitelabelSettings) {
        state.whitelabelSettings = whitelabelSettings;
    },
    setRestoreId(state, restoreId: string) {
        state.checkoutSettings.restoreId = restoreId;
    },
    restorePreInquiry(state) {
        const hsCompanyId = state.checkoutSettings.hsCompanyId;

        state.checkoutSummary = { ...state.preInquiry.bookingRequest, hsCompanyId: hsCompanyId };
    },
    setPreInquiry(state, preInquiry: PreInquiry) {
        state.preInquiry = preInquiry;
    },
    restoreCheckoutSteps (state) {
        if (!state.preInquiry.bookingStep) {
            state.preInquiry.bookingStep = 'expose';
        }

        let currentBookingStep = state.preInquiry.bookingStep;

        /**
         * The booking step 'expose' is the buzzword to direct the user back to the trip expose.
         * However, 'expose' is not a valid booking step and a blank page would be displayed in the background of RestoreInfo-Modal.
         * Therefore, currentBookingStep is overwritten.
         */
        if (currentBookingStep === 'expose') {
            currentBookingStep = BOOKING_STEPS.appointment;
        }

        const currentNavIdx = state.navigation.findIndex((nav) => nav === currentBookingStep);

        state.activeNavigation = currentBookingStep;
        state.activeIndex = currentNavIdx;
    },
    closeModals (state) {
        Object.values(state.visibility).forEach((modal) => modal.isVisible = false);
    },
    disableCheckoutSubmitBtn (state, disableSubmitButton: boolean) {
        state.disableSubmitButton = disableSubmitButton;
    },
    setCurrentFare (state, fare: string) {
        state.fare = fare;
    },
    hasPayment (state, hasPayment: boolean) {
        state.hasPayment = hasPayment;
    },
    setLoadingTexts (state, loadingTexts: string[]) {
        state.loading.loadingTexts = loadingTexts;
    },
    resetLoadingText (state) {
        state.loading.loadingTexts = CHECKOUT_DEFAULT_LOADING_TEXT;
    },
    isLoading (state, loadingState: boolean) {
        state.loading.isActive = loadingState;
    },
    removePaymentStep (state) {
        state.navigation = state.navigation.filter((bookingStep: string) => bookingStep !== 'payment');
    },
    setPaxConnectUri (state, paxConnectUri: string) {
        state.visibility.paxConnect.importUri = paxConnectUri;
    },
    setBottomSheetActiveState (state, activeState: boolean) {
        state.isBottomSheetOpen = activeState;
    },
    setSessionId(state, sessionId: string) {
        state.checkoutSettings.sessionId = sessionId;
    },
    setDepartureAirport(state, departureAirport: string|null) {
        state.checkoutSettings.departureAirport = departureAirport;
    },
    updateCheckoutSummarySessionId(state, sessionId: string) {
        state.checkoutSummary.sessionId = sessionId;
    },
    restoreBookingRequest(state, bookingRequest: BookingRequest) {
        const hsCompanyId = state.checkoutSettings.hsCompanyId;

        state.checkoutSummary = { ...bookingRequest, hsCompanyId: hsCompanyId };
    },
    restoreDealBookingStep(state, bookingStep: string) {
        const currentNavIdx = state.navigation.findIndex((nav) => nav === bookingStep);

        state.activeNavigation = bookingStep;
        state.activeIndex = currentNavIdx;
    },
    resetAssistComments(state) {
        state.assistComments = {
            appointmentComment: '',
            faresComment: '',
            flightsComment: '',
            roomsComment: '',
            accommodationComment: '',
            servicesComment: '',
            carRentalComment: '',
            personalDataComment: ''
        };
    },
    hasError(state, status: boolean) {
        state.sessionIdError = status;
    },
    isOnlyNonBinding(state, isBinding) {
        state.isOnlyNonBinding = isBinding;
    },
    setPaymentOptions(state, paymentOptions: PaymentOptions) {
        state.paymentOptions = paymentOptions;
    },
    setSiteSettings(state, siteSettings: SiteSettings) {
        state.siteSettings = siteSettings;
    },
    resetVoucher(state) {
        state.checkoutSummary.voucher = '';
    },
    setInternalCheckoutApiEndpoint(state) {
        const languageCode = state.siteSettings.basePath.replaceAll('/', '');

        state.internalApiEndpoint = `${state.siteSettings.baseUrl}${languageCode}${CHECKOUT_API}`;
    }
} as MutationTree<CheckoutState>

function isNotOptional(bookingStep: string, bookingSteps: { [index: string]: any }) {
    return !bookingSteps.hasOwnProperty(bookingStep);
}

function isEnabled(bookingStep: string, bookingSteps: { [index: string]: any }) {
    return bookingSteps[bookingStep];
}

function updateQuantityInDataLayer(amount: number) {
    const datalayer: Array<any> = window.dataLayer || [];

    if (datalayer[1].hasOwnProperty('ecommerce')) {
        datalayer[1].ecommerce.add.products[0].quantity = amount;
    }
}
